// FTUX (First-Time User Experience) Tour
// HighLevel Sub-Account Tour Guide. Brand-neutral: {{brandName}} is substituted at run time
// with the agency's app name (see tour-runner.ts) — never hardcode a brand here.

export const ftuxTour = {
  id: 'ftux',
  name: 'Platform Overview Tour',
  displayName: 'Welcome to Your Platform',
  description: 'Get familiar with your command center in just 4 minutes',
  estimatedDuration: '4 minutes',
  category: 'onboarding',
  autoStart: true, // Auto-start on first login
  
  config: {
    animate: true,
    overlayOpacity: 0.75,
    smoothScroll: true,
    allowClose: true,
    overlayClickNext: false,
    showProgress: true,
    progressText: 'Step {{current}} of {{total}}'
  },
  
  steps: [
    // Step 1: Welcome Message
    {
      popover: {
        title: '👋 Welcome to {{brandName}}!',
        description: `
          Your all-in-one platform for managing contacts, conversations, 
          and campaigns. Let's take a quick 4-minute tour to get you oriented 
          and ready to hit the ground running.
        `,
        side: 'over', // Modal style, centered
        align: 'center',
        showButtons: ['next', 'close']
      }
    },

    // Step 2: Main Navigation
    {
      element: 'nav[aria-label="header"]',
      popover: {
        title: '🧭 Your Navigation Hub',
        description: `
          This sidebar is your command center. Every tool and feature lives here. 
          We'll explore the most important sections next, so you know exactly 
          where to go for any task.
        `,
        side: 'right',
        align: 'start'
      }
    },

    // Step 3: Dashboard Overview
    {
      element: '#sb_dashboard',
      popover: {
        title: '📊 Dashboard: Your Command Center',
        description: `
          Start here every day. Your dashboard shows key metrics, pending tasks, 
          and business performance at a glance. It's your bird's eye view of 
          everything happening in your business.
        `,
        side: 'right',
        align: 'center',
        onNextClick: () => {
          // Highlight dashboard with pulse animation
          document.querySelector('#sb_dashboard')?.classList.add('tour-pulse');
        }
      }
    },

    // Step 4: Contacts
    {
      element: '#sb_contacts',
      popover: {
        title: '📋 Contacts: Your Database',
        description: `
          Every lead, client, and prospect lives here. Contacts are the foundation 
          of everything. Search, filter, tag, and communicate with your entire 
          database from this powerful hub.
        `,
        side: 'right',
        align: 'center',
        onPrevClick: () => {
          document.querySelector('#sb_dashboard')?.classList.remove('tour-pulse');
        }
      }
    },

    // Step 5: Conversations
    {
      element: '#sb_conversations',
      popover: {
        title: '💬 Conversations: Unified Inbox',
        description: `
          All your communication in one place—SMS, email, live chat, Facebook, 
          Instagram, and Google Business Messages. Never miss a message again. 
          Respond to everyone from this single, powerful inbox.
        `,
        side: 'right',
        align: 'center'
      }
    },

    // Step 6: Calendars
    {
      element: '#sb_calendars',
      popover: {
        title: '📅 Calendars: Automated Booking',
        description: `
          Let clients book time with you automatically. Set your availability 
          once, and the system handles scheduling, reminders, and confirmations. 
          Say goodbye to phone tag and hello to automatic bookings.
        `,
        side: 'right',
        align: 'center'
      }
    },

    // Step 7: Opportunities
    {
      element: '#sb_opportunities',
      popover: {
        title: '🎯 Opportunities: Sales Pipeline',
        description: `
          Visualize and manage your deals through every stage. Drag and drop 
          opportunities as they progress, track values, and get insights into 
          your sales performance. Your pipeline has never been clearer.
        `,
        side: 'right',
        align: 'center'
      }
    },

    // Step 8: Marketing & Sites
    {
      element: '#sb_email-marketing',
      popover: {
        title: '📧 Marketing: Campaigns & Social',
        description: `
          Launch email campaigns, schedule social media posts, and manage all 
          your marketing from here. Built-in templates make it easy to create 
          professional campaigns in minutes.
        `,
        side: 'right',
        align: 'center'
      }
    },

    // Step 9: Sites (Funnels & Forms)
    {
      element: '#sb_sites',
      popover: {
        title: '🌐 Sites: Funnels & Forms',
        description: `
          Build landing pages, funnels, and forms to capture leads. Use templates 
          or create from scratch. Connect your own domain and start generating 
          leads immediately.
        `,
        side: 'right',
        align: 'center'
      }
    },

    // Step 10: Automation
    {
      element: '#sb_automation',
      popover: {
        title: '⚡ Automation: Work While You Sleep',
        description: `
          Create workflows that run automatically—follow-up sequences, appointment 
          reminders, lead nurturing, and more. Set it once, and let the system 
          handle repetitive tasks for you.
        `,
        side: 'right',
        align: 'center'
      }
    },

    // Step 11: Settings
    {
      element: '[href*="/settings"]',
      popover: {
        title: '⚙️ Settings: Configure Everything',
        description: `
          Connect phone numbers, email domains, payment processors, and team members. 
          Customize your platform, integrate with other tools, and set up your 
          business profile here.
        `,
        side: 'right',
        align: 'start'
      }
    },

    // Step 12: Completion & Next Steps
    {
      popover: {
        title: '🚀 You\'re Ready to Go!',
        description: `
          Perfect! You now know your way around. Here's what to do next:
          
          • **Import your contacts** to get started
          • **Set up your calendar** for bookings
          • **Explore the template library** for quick wins
          
          Need help anytime? Look for the Help icon to open your help center.
          
          Let's build something amazing! 💪
        `,
        side: 'over',
        align: 'center',
        showButtons: ['close'],
        doneBtnText: 'Let\'s Get Started! →'
      }
    }
  ]
};
